import { createSlice } from "@reduxjs/toolkit";

const oddsSlice = createSlice({
  name: "odds",
  initialState: {
    fixtures: {},
  },
  reducers: {
    setInitialOdds: (state, action) => {
      const fixtures = {};

      action.payload.forEach((fixture) => {
        const gameId = fixture.gameId;

        // Assume one event per fixture
        const event = fixture.events[0];

        // Extract odds
        const formattedOdds = {};

        for (const [market, entries] of Object.entries(event.odds)) {
          formattedOdds[market] = {};

          entries.forEach((entry) => {
            if (entry.label !== "TradingStatus") {
              formattedOdds[market][entry.label] =
                entry.value === "null" ? null : entry.value;
            }
          });
        }

        fixtures[gameId] = {
          league: fixture.leagueName,
          date: event.eventDate,
          name: event.gameName,
          odds: formattedOdds,
        };
      });

      state.fixtures = fixtures;
    },

    updateOdds: (state, action) => {
      const { fixtureId, selectionId, odds, marketInfo, timestamp } =
        action.payload;

      // Ensure fixture exists in state
      if (!state.fixtures[fixtureId]) {
        state.fixtures[fixtureId] = {
          markets: {},
          lastUpdated: timestamp || Date.now(),
        };
      }

      // Update the fixture's last updated time
      state.fixtures[fixtureId].lastUpdated = timestamp || Date.now();

      // If we have selectionId, update the selection directly
      if (selectionId) {
        // Store selection for quick lookup
        if (!state.selections[selectionId]) {
          state.selections[selectionId] = {
            fixtureId,
            marketInfo,
            odds: {},
            lastUpdated: timestamp || Date.now(),
          };
        }

        // Update the odds for this selection
        Object.entries(odds).forEach(([oddLabel, oddValue]) => {
          const previousValue = state.selections[selectionId].odds[oddLabel];

          state.selections[selectionId].odds[oddLabel] = {
            value: oddValue,
            previousValue: previousValue?.value || oddValue,
            lastUpdated: timestamp || Date.now(),
            changed: previousValue?.value !== oddValue,
          };
        });

        // Also update in the fixture structure for backwards compatibility
        if (marketInfo?.marketName) {
          const marketName = marketInfo.marketName;

          if (!state.fixtures[fixtureId].markets[marketName]) {
            state.fixtures[fixtureId].markets[marketName] = {
              selections: {},
              marketInfo,
            };
          }

          // Update the market structure
          Object.entries(odds).forEach(([oddLabel, oddValue]) => {
            if (
              !state.fixtures[fixtureId].markets[marketName].selections[
                oddLabel
              ]
            ) {
              state.fixtures[fixtureId].markets[marketName].selections[
                oddLabel
              ] = {
                selectionId,
                odds: {},
              };
            }

            state.fixtures[fixtureId].markets[marketName].selections[oddLabel] =
              {
                selectionId,
                odds: {
                  value: oddValue,
                  previousValue:
                    state.selections[selectionId]?.odds[oddLabel]
                      ?.previousValue || oddValue,
                  lastUpdated: timestamp || Date.now(),
                  changed:
                    state.selections[selectionId]?.odds[oddLabel]?.changed ||
                    false,
                },
              };
          });
        }
      } else {
        // Fallback: update without selectionId (less precise)
        Object.entries(odds).forEach(([oddLabel, oddValue]) => {
          // Try to find existing selection by oddLabel across markets
          let found = false;

          Object.values(state.fixtures[fixtureId].markets || {}).forEach(
            (market) => {
              if (market.selections[oddLabel]) {
                const previousValue = market.selections[oddLabel].odds.value;
                market.selections[oddLabel].odds = {
                  value: oddValue,
                  previousValue,
                  lastUpdated: timestamp || Date.now(),
                  changed: previousValue !== oddValue,
                };
                found = true;
              }
            }
          );

          // If not found, create a generic entry
          if (!found) {
            console.warn(
              `Could not find selection for oddLabel: ${oddLabel} in fixture: ${fixtureId}`
            );
          }
        });
      }
    },
  },
});

export const { setInitialOdds, updateOdds } = oddsSlice.actions;
export const selectFixtures = (state) => state.odds.fixtures;
export const oddsSliceReducer = oddsSlice.reducer;
